import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { Dispatch } from 'redux';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { ScreenReaderText } from '@atlassian/jira-accessibility';
import {
	categoryIdForStatusCategory,
	DONE,
} from '@atlassian/jira-common-constants/src/status-categories';
import ExperienceContext from '@atlassian/jira-common-experience-tracking-viewing/src/view/common/context';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey, useProjectKey } from '@atlassian/jira-issue-context-service';
import { useFieldValue, useSearchCustomFieldKeys } from '@atlassian/jira-issue-field-base';
import { useFlaggedField } from '@atlassian/jira-issue-field-flagged';
import { ResolutionField, useResolutionField } from '@atlassian/jira-issue-field-resolution';
import type { StatusTransition, StatusValue } from '@atlassian/jira-issue-field-status';
import type { OnStatusChangeSuccess } from '@atlassian/jira-issue-field-status/src/services/status-service/types';
import type { FieldOptions } from '@atlassian/jira-issue-view-common-types/src/connect-field-type';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type';
import { flowWithSafeComponent } from '@atlassian/jira-issue-view-common-utils';
import { STATUS } from '@atlassian/jira-issue-view-configurations';
import {
	withEditExperienceTracker,
	withViewExperienceTracker,
} from '@atlassian/jira-issue-view-experience-tracking';
import { connect } from '@atlassian/jira-issue-view-react-redux';
import { isCompletedLoadingSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/issue-selector';
import {
	fieldEditCancel,
	type FieldEditCancelAction,
} from '@atlassian/jira-issue-view-store/src/issue-field/state/actions/field-actions';
import {
	type FieldSaveRequestAction,
	fieldSaveSuccess,
	fieldUpdated,
	type FieldUpdatedAction,
} from '@atlassian/jira-issue-view-store/src/issue-field/state/actions/field-save-actions';
import {
	useWorkflowTransitionsActions,
	useWorkflowTransitionsTransitionInProgress,
} from '@atlassian/jira-issue-workflow-transitions-services';
import {
	FLAGGED_CF_TYPE,
	HIERARCHY_LEVEL_SAME_LEVEL,
	STATUS_TYPE,
} from '@atlassian/jira-platform-field-config';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectType } from '@atlassian/jira-project-context-service/src/main';
import ActionsMenu from './actions';
import FlaggedField from './flagged-field';
import { messages } from './messages';
import StatusFieldWithRefWrapper from './status-field-with-wrapper';
import { getStatusInitialValue } from './utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OwnProps = Record<any, any>;
type StateProps = {
	isEditable: boolean;
};
type DispatchProps = {
	onStatusChanged: OnStatusChangeSuccess;
	onEditCancel: () => void;
};
export type Props = OwnProps & StateProps & DispatchProps;
type Action = FieldSaveRequestAction | FieldUpdatedAction | FieldEditCancelAction;

export const StatusView = ({ isEditable, onStatusChanged, onEditCancel }: Props) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey();
	const projectType = useProjectType(projectKey);
	const experienceContext = useContext(ExperienceContext);

	const [statusFieldValue] = useFieldValue({ issueKey, fieldKey: STATUS });

	const [{ value: resolutionFieldValue, fieldConfig: resolutionFieldConfig }] = useResolutionField({
		issueKey,
	});
	const resolution = resolutionFieldValue?.name;
	const transitionInProgress = useWorkflowTransitionsTransitionInProgress();
	const [, workflowTransitionsActions] = useWorkflowTransitionsActions();

	const initialValue = useMemo(() => getStatusInitialValue(statusFieldValue), [statusFieldValue]);

	// This is needed for JSW's monitoring of Issue Hierarchy in Next-gen projects
	const [sameLevel] = useFieldValue({ issueKey, fieldKey: HIERARCHY_LEVEL_SAME_LEVEL });
	const issueHierarchyLevel = sameLevel?.level;

	const [flaggedFieldKey] = useSearchCustomFieldKeys(issueKey, FLAGGED_CF_TYPE) || '';
	const [{ value: isIssueFlagged }] = useFlaggedField({ issueKey, fieldKey: flaggedFieldKey });

	const [prevIsIssueFlagged] = useState(isIssueFlagged);
	const [shouldAnnounce, setShouldAnnounce] = useState<boolean>(false);

	const onStatusSubmitted = (
		oldValue: StatusValue,
		newValue: StatusValue,
		meta: {
			transition: StatusTransition;
		},
		event: UIAnalyticsEvent,
	) => {
		workflowTransitionsActions.setTransitionInProgress(true);
		fireTrackAnalytics(event, 'issueStatus updated', {
			oldValId: oldValue.id,
			oldStatusCategoryId: oldValue.statusCategory.id,
			oldStatusCategoryName: oldValue.statusCategory.name,
			newValId: newValue.id,
			newStatusCategoryId: newValue.statusCategory.id,
			newStatusCategoryName: newValue.statusCategory.name,
			hasScreen: meta.transition.hasScreen,
			isConditional: meta.transition.isConditional,
			issueHierarchyLevel,
		});
	};

	const onStatusChangeFailure = () => {
		workflowTransitionsActions.setTransitionInProgress(false);
	};

	useEffect(() => {
		experienceContext?.consumerCallbacks.onSuccess();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			(prevIsIssueFlagged === null && isIssueFlagged !== null) ||
			(prevIsIssueFlagged !== null && isIssueFlagged === null)
		) {
			setShouldAnnounce(true);
		}
	}, [isIssueFlagged, prevIsIssueFlagged]);

	return (
		<ErrorBoundary
			onError={(location: string, error: Error) => {
				experienceContext?.consumerCallbacks.onFailure(STATUS_TYPE, {
					errorMessage: error.message,
				});
			}}
		>
			<Container>
				<StatusFieldContainer
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="issue.views.issue-base.foundation.status.status-field-wrapper"
				>
					<StatusFieldWithRefWrapper
						issueKey={issueKey}
						projectType={projectType}
						initialValue={initialValue}
						isEditable={isEditable}
						appearance="button"
						onSubmit={onStatusSubmitted}
						onSuccess={onStatusChanged}
						onEditCancel={onEditCancel}
						onFailure={onStatusChangeFailure}
						preFetchTransitions
						registerInCommandPalette
					/>
				</StatusFieldContainer>
				{shouldAnnounce && (
					<ScreenReaderText role="status" aria-live="polite">
						{isIssueFlagged
							? formatMessage(messages.flagAdded)
							: formatMessage(messages.flagRemoved)}
					</ScreenReaderText>
				)}

				{isIssueFlagged && (
					<StatusComponentWrapper
						/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
						data-testid="issue.views.issue-base.foundation.status.flagged-field-wrapper"
					>
						<FlaggedField externalId="issue.status.flagged" />
					</StatusComponentWrapper>
				)}
				{!transitionInProgress &&
					((resolution !== undefined && resolution !== null) ||
						(resolutionFieldConfig?.isEditable &&
							initialValue.statusCategory.id === categoryIdForStatusCategory(DONE))) && (
						<div
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							data-testid="issue.views.issue-base.foundation.status.resolution"
						>
							<ResolutionField issueKey={issueKey} />
						</div>
					)}
				{!transitionInProgress && (
					<StatusComponentWrapper
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						data-testid="issue.views.issue-base.foundation.status.actions-wrapper"
					>
						<ActionsMenu
							initialStatusValue={initialValue}
							onSubmit={onStatusSubmitted}
							onSuccess={onStatusChanged}
						/>
					</StatusComponentWrapper>
				)}
			</Container>
		</ErrorBoundary>
	);
};

// dummy field options that are not used anywhere, but they are required in the fieldSaveSuccess action.
export const fieldOptions: FieldOptions<unknown> = {
	canContainMediaContent: false,
	isOptimistic: false,
	isRichTextField: false,
	shouldSaveDraft: false,
	shouldDiscardChangeOnFailure: false,
	onSaveFailureFlagType: null,
	onSaveSuccessFlagType: null,
	saveField: () => Promise.resolve(),
};

// FIXME: JIV-17455 should be fully typed
export default flowWithSafeComponent(
	// @ts-expect-error - Argument of type 'InferableComponentEnhancerWithProps<StateProps & DispatchProps, {}>' is not assignable to parameter of type 'FlowStep<C, ComponentType<ComponentBeingTrackedProps<{ [k in keyof (DistributiveOmit<LibraryManagedAttributes<C, GetProps<C>>, Extract<"isEditable", keyof LibraryManagedAttributes<C, GetProps<C>>> | Extract<...> | Extract<...>> & {} & ConnectPropsMaybeWithoutContext<...>)]: (DistributiveOmit<...> & ... 1 more ... & ...'.
	connect(
		(state: State): StateProps => ({
			isEditable: isCompletedLoadingSelector(state),
		}),
		(dispatch: Dispatch<Action>): DispatchProps => ({
			onStatusChanged: (status, viaDialog) => {
				/* Ideally we would only dispatch fieldUpdated here, but there are still internal components listening to the changing
			   values that fieldSaveSuccess provides for status field. For now, let's fire both */
				// @ts-expect-error - TS2554 - Expected 4-5 arguments, but got 3.
				dispatch(fieldSaveSuccess(STATUS, fieldOptions, status));
				dispatch(fieldUpdated(STATUS, status, { viaDialog }));
			},
			onEditCancel: () => {
				/* TODO: this is being called twice when the transition dialog is opened and we close it by using the "escape" key.
			   This happens because the 'keydown' event is being handled by
			   the dialog itself *and* by the AK Select component. This is a bug on AK Select, since it
			   should remove that event listener when the component unmounts. I've raised a ticket
			   with Atlaskit to fix this: https://ecosystem.atlassian.net/projects/AK/queues/issue/AK-6310 */
				dispatch(fieldEditCancel(STATUS, fieldOptions));
			},
		}),
	), // TODO: Temporarily use analytic HoC until new hook based API is ready.
	withViewExperienceTracker(STATUS),
	withEditExperienceTracker(STATUS),
)(StatusView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
	paddingTop: token('space.150', '12px'),

	paddingBottom: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > *:not(:last-child)': {
		marginRight: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusFieldContainer = styled.div({
	paddingRight: 'unset',
	paddingBottom: token('space.150', '12px'),
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusComponentWrapper = styled.div({
	paddingBottom: token('space.150', '12px'),
	display: 'inline-flex',
	flexWrap: 'nowrap',
	maxWidth: '100%',
});
